/**
 * ContactRegistrationButton - Floating button for public contact registration
 *
 * Used in Santinho Digital templates to allow visitors to register as supporters.
 */

import { useState, useCallback } from 'react';
import { useTenant } from '@/contexts';
import { isValidPhone } from '@/types/contact.types';
import { Turnstile } from '@/components/Turnstile';

interface ContactRegistrationButtonProps {
  buttonText?: string;
  buttonClassName?: string;
  position?: 'fixed' | 'inline';
}

export function ContactRegistrationButton({
  buttonText = 'Quero Apoiar',
  buttonClassName,
  position = 'fixed',
}: ContactRegistrationButtonProps) {
  const { tenant } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cepLoading, setCepLoading] = useState(false);

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    cep: '',
    city: '',
    state: '',
    neighborhood: '',
  });

  const fetchCep = useCallback(
    async (cep: string): Promise<{ city: string; state: string; neighborhood: string } | null> => {
      const cleanCep = cep.replace(/\D/g, '');
      if (cleanCep.length !== 8) return null;

      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await response.json();
        if (!data.erro) {
          return {
            city: data.localidade || '',
            state: data.uf || '',
            neighborhood: data.bairro || '',
          };
        }
      } catch {
        // ViaCEP offline
      }
      return null;
    },
    []
  );

  const handleCepBlur = useCallback(async () => {
    if (formData.city) return; // Already fetched
    setCepLoading(true);
    const result = await fetchCep(formData.cep);
    if (result) {
      setFormData((prev) => ({ ...prev, ...result }));
    }
    setCepLoading(false);
  }, [formData.cep, formData.city, fetchCep]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      // Validate
      if (!formData.name.trim()) {
        setError('Nome é obrigatório');
        return;
      }

      if (!formData.phone.trim()) {
        setError('Telefone é obrigatório');
        return;
      }

      if (!isValidPhone(formData.phone)) {
        setError('Telefone inválido. Use formato com DDD (ex: 11999999999)');
        return;
      }

      if (!tenant?.slug) {
        setError('Erro ao identificar a campanha');
        return;
      }

      setIsSubmitting(true);

      try {
        // Fetch CEP data if user hasn't waited for blur
        let city = formData.city.trim();
        let state = formData.state;
        let neighborhood = formData.neighborhood.trim();
        const cleanCep = formData.cep.replace(/\D/g, '');

        if (cleanCep.length === 8 && !city) {
          const cepData = await fetchCep(formData.cep);
          if (cepData) {
            city = cepData.city;
            state = cepData.state;
            neighborhood = cepData.neighborhood;
          }
        }

        // Use base API URL without /api suffix for public endpoints
        const baseUrl = (import.meta.env.VITE_API_URL || '/api').replace(/\/api$/, '');
        const response = await fetch(`${baseUrl}/public/contacts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-Slug': tenant.slug,
          },
          body: JSON.stringify({
            name: formData.name.trim(),
            phone: formData.phone.trim(),
            zipCode: cleanCep || undefined,
            city: city || undefined,
            state: state || undefined,
            neighborhood: neighborhood || undefined,
            turnstileToken,
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error?.message || 'Erro ao realizar cadastro');
        }

        setIsSuccess(true);
        setTurnstileToken(null);
        setFormData({ name: '', phone: '', cep: '', city: '', state: '', neighborhood: '' });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao realizar cadastro');
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, tenant?.slug]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsSuccess(false);
    setError(null);
  }, []);

  const primaryColor = tenant?.theme?.primaryColor || '#1d4ed8';

  const defaultButtonClass =
    position === 'fixed'
      ? 'fixed bottom-6 right-6 z-40 px-6 py-3 rounded-full text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105'
      : 'px-6 py-3 rounded-full text-white font-semibold shadow-lg hover:shadow-xl transition-all';

  return (
    <>
      {/* Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={buttonClassName || defaultButtonClass}
        style={{ backgroundColor: primaryColor }}
      >
        {buttonText}
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 text-white" style={{ backgroundColor: primaryColor }}>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">
                  {isSuccess ? 'Cadastro Realizado!' : 'Faça Parte da Campanha'}
                </h2>
                <button
                  onClick={handleClose}
                  className="text-white/80 hover:text-white text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              {!isSuccess && (
                <p className="text-white/80 mt-1">Cadastre-se para receber novidades</p>
              )}
            </div>

            {/* Content */}
            <div className="p-6">
              {isSuccess ? (
                <div className="text-center py-4">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: `${primaryColor}20` }}
                  >
                    <svg
                      className="w-8 h-8"
                      style={{ color: primaryColor }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Obrigado por se cadastrar!
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Em breve você receberá novidades sobre a campanha.
                  </p>
                  <button
                    onClick={handleClose}
                    className="px-6 py-2 rounded-full text-white font-medium"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Fechar
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  {/* Name */}
                  <div>
                    <label
                      htmlFor="reg-name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Nome completo *
                    </label>
                    <input
                      id="reg-name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
                      style={{ focusRingColor: primaryColor } as React.CSSProperties}
                      placeholder="Seu nome"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="reg-phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Telefone (WhatsApp) *
                    </label>
                    <input
                      id="reg-phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
                      placeholder="11999999999"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* CEP */}
                  <div>
                    <label
                      htmlFor="reg-cep"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      CEP
                    </label>
                    <div className="relative">
                      <input
                        id="reg-cep"
                        type="text"
                        value={formData.cep}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, 8);
                          const formatted = v.length > 5 ? `${v.slice(0, 5)}-${v.slice(5)}` : v;
                          setFormData((prev) => ({ ...prev, cep: formatted }));
                        }}
                        onBlur={handleCepBlur}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
                        placeholder="00000-000"
                        maxLength={9}
                        disabled={isSubmitting}
                      />
                      {cepLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    {formData.city && (
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.city}{formData.state ? ` - ${formData.state}` : ''}
                        {formData.neighborhood ? ` (${formData.neighborhood})` : ''}
                      </p>
                    )}
                  </div>

                  {/* Turnstile */}
                  <div className="flex justify-center">
                    <Turnstile
                      onVerify={setTurnstileToken}
                      onExpire={() => setTurnstileToken(null)}
                      size="compact"
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting || !turnstileToken}
                    className="w-full py-3 rounded-lg text-white font-semibold transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {isSubmitting ? 'Cadastrando...' : 'Cadastrar'}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    Ao se cadastrar, você concorda em receber comunicações da campanha.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
